import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import {
  Tractor, Fuel, Droplets, Clock, Activity, AlertCircle, MapPin,
  Loader2, ChevronRight, Wifi, WifiOff,
} from "lucide-react";

const STATUS_BADGE = {
  ACTIVE:  { cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: Activity, label: "Active" },
  IDLE:    { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Wifi, label: "Idle" },
  OFFLINE: { cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: WifiOff, label: "Offline" },
};

function GaugeBar({ label, pct, icon: Icon, warnBelow = 20 }) {
  const hasPct = typeof pct === "number" && !Number.isNaN(pct);
  const danger = hasPct && pct < warnBelow;
  const display = hasPct ? `${Math.round(pct)}%` : "—";
  const width = hasPct ? Math.max(0, Math.min(100, pct)) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px]">
        <span className="flex items-center gap-1 text-slate-400"><Icon size={11} /> {label}</span>
        <span className={`tabular-nums font-semibold ${danger ? "text-red-300" : "text-slate-200"}`}>{display}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded overflow-hidden">
        <div className={`h-full ${danger ? "bg-red-400" : "bg-amber-400"}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function MachineCard({ m }) {
  const s = STATUS_BADGE[m.status] || STATUS_BADGE.OFFLINE;
  const SIcon = s.icon;
  const lastSeenDate = m.last_seen ? new Date(m.last_seen) : null;
  const lastSeenValid = lastSeenDate && !Number.isNaN(lastSeenDate.getTime());
  const mins = lastSeenValid ? Math.round((Date.now() - lastSeenDate.getTime()) / 60000) : null;
  const lastSeenLabel = mins == null ? "—"
    : mins < 60 ? `${mins} min ago`
    : `${Math.round(mins / 60)}h ago`;
  const hours = typeof m.operating_hours === "number" ? `${m.operating_hours.toFixed(1)}h` : "—";
  const speed = typeof m.speed_kph === "number" ? `${m.speed_kph} kph` : "—";
  return (
    <Link to={`/admin/fleet/${m.vin}`} data-testid={`fleet-card-${m.vin}`}
          className="group block bg-[#0a0e1c] border border-white/10 rounded-lg p-5 hover:border-amber-400/50 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300/80">{m.category || "Tractor"}</div>
          <h3 className="font-display font-bold text-lg text-slate-100 mt-0.5">{m.nickname || m.vin}</h3>
          <div className="text-xs text-slate-400">
            {m.make || ""} {m.model || ""}{m.year ? ` · ${m.year}` : ""}
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${s.cls}`}>
          <SIcon size={10} /> {s.label}
        </span>
      </div>

      <div className="space-y-2.5 mb-4">
        <GaugeBar label="Fuel" pct={m.fuel_remaining_pct} icon={Fuel} />
        <GaugeBar label="DEF / AdBlue" pct={m.def_remaining_pct} icon={Droplets} warnBelow={25} />
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs pt-3 border-t border-white/5">
        <div>
          <div className="text-slate-500 text-[10px] uppercase tracking-widest">Hours</div>
          <div className="text-slate-200 font-semibold tabular-nums mt-0.5">{hours}</div>
        </div>
        <div>
          <div className="text-slate-500 text-[10px] uppercase tracking-widest">Speed</div>
          <div className="text-slate-200 font-semibold tabular-nums mt-0.5">{speed}</div>
        </div>
        <div className="col-span-2">
          <div className="text-slate-500 text-[10px] uppercase tracking-widest flex items-center gap-1"><Clock size={10} /> Last ping</div>
          <div className="text-slate-200 mt-0.5">{lastSeenLabel}{m.operator ? <span className="text-slate-500"> · {m.operator}</span> : ""}</div>
        </div>
      </div>

      <div className="mt-3 text-[11px] text-amber-400 group-hover:text-amber-300 flex items-center justify-end gap-1">
        View details <ChevronRight size={12} />
      </div>
    </Link>
  );
}

export default function Fleet() {
  const { api } = useTenant();
  const [fleet, setFleet] = useState([]);
  const [status, setStatus] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/fieldops/fleet"), api.get("/fieldops/status")])
      .then(([fr, sr]) => {
        const payload = fr.data;
        setFleet(Array.isArray(payload) ? payload : (payload?.data || []));
        setStatus({ ...sr.data, _source: payload?.source });
        if (payload?.source === "mock_fallback") {
          setFallback({
            reason: payload.reason,
            status: payload.status,
            error: payload.error,
          });
        }
      })
      .catch(e => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const summary = {
    total: fleet.length,
    active: fleet.filter(m => m.status === "ACTIVE").length,
    idle: fleet.filter(m => m.status === "IDLE").length,
    offline: fleet.filter(m => m.status === "OFFLINE").length,
    lowFuel: fleet.filter(m => m.fuel_remaining_pct < 25).length,
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mb-1">
            <Tractor size={13} /> FieldOps · New Holland fleet
          </div>
          <h1 className="text-2xl font-bold">Fleet overview</h1>
          {status && (
            <div className="text-xs text-slate-400 mt-1 flex items-center gap-3 flex-wrap">
              <span>Region: <strong className="text-slate-300">{status.region}</strong></span>
              {status.force_mock ? (
                <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold">
                  MOCKED — set MOCK_FIELDOPS=false to go live
                </span>
              ) : status.connected ? (
                <span className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold">
                  Live · {status.user_email}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold">
                  Demo data — connect to go live
                </span>
              )}
            </div>
          )}
        </div>
        <Link to="/admin/fleet/map" data-testid="fleet-map-link"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-4 py-2 rounded-md text-sm">
          <MapPin size={14} /> Map view
        </Link>
        <Link to="/admin/fleet/operations" data-testid="fleet-ops-link"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-4 py-2 rounded-md text-sm ml-2">
          Operations
        </Link>
        <Link to="/admin/fleet/faults" data-testid="fleet-faults-link"
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 px-4 py-2 rounded-md text-sm ml-2">
          Faults
        </Link>
        <button onClick={async () => {
          try {
            const r = await api.get("/fieldops/raw");
            const w = window.open("", "cnh_debug", "width=900,height=700");
            w.document.write(`<pre style="font:12px monospace;padding:20px;background:#0a0e1c;color:#cbd5e1;">${JSON.stringify(r.data, null, 2).replace(/[<>&]/g, c => ({"<":"&lt;",">":"&gt;","&":"&amp;"}[c]))}</pre>`);
          } catch (e) { alert(e.response?.data?.detail || e.message); }
        }} data-testid="fleet-debug" title="Show raw CNH response"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 px-3 py-2 rounded-md text-sm ml-2">
          🔍 Debug
        </button>
        <Link to="/admin/fleet/connect" data-testid="fleet-connect-link"
              className="inline-flex items-center gap-2 bg-[#004B98] hover:bg-[#003A7A] text-white px-4 py-2 rounded-md text-sm font-semibold ml-2">
          <Tractor size={14} /> Connect New Holland
        </Link>
      </div>

      {err && <div className="bg-red-500/10 border border-red-500/30 rounded-md px-4 py-2.5 text-sm text-red-200 mb-4">{err}</div>}

      {fallback && status?.connected && !status?.force_mock && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3 text-xs text-amber-200 mb-4">
          <div className="font-bold uppercase tracking-widest mb-1">
            CNH live call failed — showing demo data
          </div>
          <div className="mt-1 grid sm:grid-cols-3 gap-2 text-slate-300">
            <div><span className="text-slate-500">Reason:</span> <code className="text-amber-100">{fallback.reason || "—"}</code></div>
            <div><span className="text-slate-500">HTTP:</span> <code className="text-amber-100">{fallback.status ?? "—"}</code></div>
            <div className="sm:col-span-3 break-all"><span className="text-slate-500">Response:</span> <code className="text-amber-100">{fallback.error || "—"}</code></div>
          </div>
          <div className="mt-2 text-slate-400">
            Tip: use <code className="text-amber-100">/api/fieldops/probe?path=/your/cnh/path</code> to find the correct endpoint from your CNH developer-portal swagger, then set <code className="text-amber-100">CNH_PATH_FLEET</code> etc in production env vars.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { v: summary.total,   l: "Machines", c: "text-slate-100" },
          { v: summary.active,  l: "Active",   c: "text-emerald-300" },
          { v: summary.idle,    l: "Idle",     c: "text-amber-300" },
          { v: summary.offline, l: "Offline",  c: "text-red-300" },
          { v: summary.lowFuel, l: "Low fuel", c: "text-red-300" },
        ].map(s => (
          <div key={s.l} className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
            <div className={`text-3xl font-display font-black ${s.c}`}>{s.v}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading fleet…
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fleet.map(m => <MachineCard key={m.vin} m={m} />)}
        </div>
      )}
    </div>
  );
}
